import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../supabase/supabaseClient'

const AuthContext = createContext({})

export const useAuth = () => {
  return useContext(AuthContext)
}

// How long subscription data is considered fresh before we'll refetch
const SUBSCRIPTION_STALE_MS = 5 * 60 * 1000 // 5 minutes

// sessionStorage keys
const CACHE_KEY_ACTIVE = 'subscriptionActive'
const CACHE_KEY_HAS_LICENSE = 'hasActiveLicense'
const CACHE_KEY_HAD_LICENSE = 'hadActiveLicense'
const CACHE_KEY_HAS_SUBSCRIPTION = 'hasActiveSubscription'

// Helper to safely read a cached boolean from sessionStorage
const readCachedBool = (key) => {
  try {
    const cached = sessionStorage.getItem(key)
    return cached !== null ? JSON.parse(cached) : null
  } catch {
    return null
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // Initialize from sessionStorage so a hard refresh doesn't flash a loader
  const [subscriptionActive, setSubscriptionActiveState] = useState(() =>
    readCachedBool(CACHE_KEY_ACTIVE)
  )
  const [hasActiveLicense, setHasActiveLicenseState] = useState(
    () => readCachedBool(CACHE_KEY_HAS_LICENSE) ?? false
  )
  const [hadActiveLicense, setHadActiveLicenseState] = useState(
    () => readCachedBool(CACHE_KEY_HAD_LICENSE) ?? false
  )
  const [hasActiveSubscription, setHasActiveSubscriptionState] = useState(
    () => readCachedBool(CACHE_KEY_HAS_SUBSCRIPTION) ?? false
  )

  // Only true on the very first subscription check — silent on revalidation
  const [subscriptionLoading, setSubscriptionLoading] = useState(
    () => readCachedBool(CACHE_KEY_ACTIVE) === null
  )

  // Refs for things that shouldn't trigger re-renders
  const hasCheckedSubscriptionRef = useRef(
    readCachedBool(CACHE_KEY_ACTIVE) !== null
  )
  const isCheckingRef = useRef(false)
  const lastCheckedRef = useRef(0)
  const lastCheckedEmailRef = useRef(null)
  const prevUserIdRef = useRef(null)

  // Persisted setters — keep state and sessionStorage in sync
  const setSubscriptionActive = (value) => {
    setSubscriptionActiveState(value)
    try {
      if (value === null) {
        sessionStorage.removeItem(CACHE_KEY_ACTIVE)
      } else {
        sessionStorage.setItem(CACHE_KEY_ACTIVE, JSON.stringify(value))
      }
    } catch { }
  }

  const setHasActiveLicense = (value) => {
    setHasActiveLicenseState(value)
    try {
      sessionStorage.setItem(CACHE_KEY_HAS_LICENSE, JSON.stringify(value))
    } catch { }
  }

  const setHadActiveLicense = (value) => {
    setHadActiveLicenseState(value)
    try {
      sessionStorage.setItem(CACHE_KEY_HAD_LICENSE, JSON.stringify(value))
    } catch { }
  }

  const setHasActiveSubscription = (value) => {
    setHasActiveSubscriptionState(value)
    try {
      sessionStorage.setItem(
        CACHE_KEY_HAS_SUBSCRIPTION,
        JSON.stringify(value)
      )
    } catch { }
  }

  const clearSubscriptionCache = () => {
    try {
      sessionStorage.removeItem(CACHE_KEY_ACTIVE)
      sessionStorage.removeItem(CACHE_KEY_HAS_LICENSE)
      sessionStorage.removeItem(CACHE_KEY_HAD_LICENSE)
      sessionStorage.removeItem(CACHE_KEY_HAS_SUBSCRIPTION)
    } catch { }
  }

  // Check user's subscription status.
  // By default, skips if recently checked. Pass force=true after a purchase
  // (e.g. via refreshSubscriptionStatus) to always refetch.
  const checkSubscriptionStatus = async (userEmail, force = false) => {
    if (!userEmail) return

    // Concurrency guard — prevent overlapping calls
    if (isCheckingRef.current) return

    // Stale-time guard — skip if we just checked this same email
    const isSameEmail = lastCheckedEmailRef.current === userEmail
    const isFresh = Date.now() - lastCheckedRef.current < SUBSCRIPTION_STALE_MS
    if (!force && isSameEmail && isFresh && hasCheckedSubscriptionRef.current) {
      return
    }

    isCheckingRef.current = true

    // Only show the loader on the very first check
    if (!hasCheckedSubscriptionRef.current) {
      setSubscriptionLoading(true)
    }

    try {
      const { data: subscriptionData, error: subError } = await supabase
        .from('subscriptions')
        .select('status, ends_at')
        .eq('customer_email', userEmail)
        .in('status', ['active', 'on_trial', 'past_due', 'cancelled'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (subError) {
        console.error('  - ❌ Subscription database error:', subError)
      }

      if (subscriptionData) {
        const isCancelledButValid =
          subscriptionData.status === 'cancelled' &&
          subscriptionData.ends_at &&
          new Date(subscriptionData.ends_at) > new Date()

        const isActive =
          ['active', 'on_trial', 'past_due'].includes(
            subscriptionData.status
          ) || isCancelledButValid

        setHasActiveSubscription(isActive)

        if (isActive) {
          setSubscriptionActive(true)
          return // ✅ stop here, no need to check licenses
        }
      } else {
        setHasActiveSubscription(false)
      }

      // Only reaches here if no valid subscription found
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser()

      if (authUser) {
        const { data: anyLicenseData, error: anyLicenseError } = await supabase
          .from('user_licenses')
          .select('expires_at')
          .eq('user_id', authUser.id)
          .limit(1)
          .maybeSingle()

        if (anyLicenseError)
          console.error('  - ❌ Any license check error:', anyLicenseError)
        setHadActiveLicense(!!anyLicenseData)

        const { data: licenseData, error: licenseError } = await supabase
          .from('user_licenses')
          .select('expires_at')
          .eq('user_id', authUser.id)
          .gt('expires_at', new Date().toISOString())
          .limit(1)
          .maybeSingle()

        if (licenseError)
          console.error('  - ❌ Active license database error:', licenseError)
        setHasActiveLicense(!!licenseData)

        if (licenseData) {
          setSubscriptionActive(true)
          return
        }
      }

      // console.log('subscriptionData:', subscriptionData)
      setSubscriptionActive(false)
    } catch (err) {
      console.error('  - 💥 Exception:', err)
      setSubscriptionActive(false)
    } finally {
      isCheckingRef.current = false
      hasCheckedSubscriptionRef.current = true
      lastCheckedRef.current = Date.now()
      lastCheckedEmailRef.current = userEmail
      setSubscriptionLoading(false)
    }
  }

  // nienke updating 3 nov so that you will not see a different hex color after logging in with google:
  const handleLoginRedirect = () => {
    const redirectPath =
      localStorage.getItem('redirectAfterLogin') ||
      sessionStorage.getItem('redirectAfterLogin') ||
      window.redirectAfterLogin

    if (redirectPath) {
      // Clean up storage
      localStorage.removeItem('redirectAfterLogin')
      sessionStorage.removeItem('redirectAfterLogin')
      delete window.redirectAfterLogin

      // Check if it's an absolute URL from a different domain
      try {
        const url = new URL(redirectPath, window.location.origin)
        const targetPath = url.pathname + url.search + url.hash
        // Use history API instead of window.location to avoid reload
        window.history.replaceState({}, '', targetPath)
      } catch (e) {
        window.history.replaceState({}, '', redirectPath)
      }
    }
  }

  useEffect(() => {
    let mounted = true

    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!mounted) return

      // Seed prevUserIdRef so the first SIGNED_IN/INITIAL_SESSION event
      // after page load isn't treated as a real auth change
      prevUserIdRef.current = session?.user?.id ?? null

      setUser(session?.user ?? null)

      if (!session?.user) {
        setSubscriptionActive(false)
        setSubscriptionLoading(false)
        hasCheckedSubscriptionRef.current = true
      } else {
        checkSubscriptionStatus(session.user.email)
      }

      setLoading(false)
    }

    getSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return

      // Supabase fires SIGNED_IN, INITIAL_SESSION, TOKEN_REFRESHED etc.
      // on tab focus and other non-events. Only act when the user
      // actually changes (real sign-in / sign-out / account switch).
      const newUserId = session?.user?.id ?? null
      if (newUserId === prevUserIdRef.current) {
        return
      }

      prevUserIdRef.current = newUserId

      if (newUserId) {
        // Genuine sign-in (or account switch)
        setUser(session.user)
        handleLoginRedirect()
        checkSubscriptionStatus(session.user.email)
        setLoading(false)
      } else {
        // Genuine sign-out
        setUser(null)
        setSubscriptionActive(false)
        setHasActiveLicense(false)
        setHadActiveLicense(false)
        setHasActiveSubscription(false)
        clearSubscriptionCache()
        // Reset refs so next sign-in does a fresh check
        hasCheckedSubscriptionRef.current = false
        lastCheckedRef.current = 0
        lastCheckedEmailRef.current = null
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription?.unsubscribe()
    }
  }, [])

  // Function to manually refresh subscription status (useful after subscribing)
  const refreshSubscriptionStatus = async () => {
    if (user?.email) {
      await checkSubscriptionStatus(user.email, true) // force=true bypasses stale check
    }
  }

  // Sign up with email and password (keeping for backward compatibility)
  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    return { data, error }
  }

  // Sign in with email and password (keeping for backward compatibility)
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  }

  // Send verification code to email (works for both existing and new users)
  const sendVerificationCode = async (email) => {
    try {
      // Send OTP - this will work for both existing and new users
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true, // Allow creating new users
          emailRedirectTo: undefined, // Ensure OTP instead of magic link
        },
      })

      if (error) {
        return { error }
      }

      return { data, error: null }
    } catch (err) {
      return { error: { message: 'Failed to send verification code' } }
    }
  }

  // Verify the OTP code (works for both sign-in and sign-up)
  const verifyCode = async (email, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })
    return { data, error }
  }

  const signOut = async () => {
    try {
      // Try the official Supabase signOut first
      const { error } = await supabase.auth.signOut()

      if (!error) {
        // Official signOut worked - we're done!
        // (the SIGNED_OUT event handler will clear cache and reset state)
        return { error: null }
      }

      // Check if session still exists after failed signOut
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (session) {
        // Manual cleanup as fallback
        localStorage.removeItem('sb-hfizqqbythrxppxhsxed-auth-token')

        // Verify the session is now cleared
        const {
          data: { session: afterSession },
        } = await supabase.auth.getSession()

        if (!afterSession) {
          // Manual cleanup worked
          setUser(null)
          setSubscriptionActive(false)
          clearSubscriptionCache()
          return { error: null }
        } else {
          // Even manual cleanup didn't work - this shouldn't happen
          console.error('❌ Manual cleanup failed - session still exists')
          return { error: new Error('Logout failed completely') }
        }
      } else {
        // Session was cleared despite the error - treat as success
        clearSubscriptionCache()
        return { error: null }
      }
    } catch (err) {
      console.error('❌ Logout process threw an exception:', err)

      // Last resort fallback on exception
      try {
        localStorage.removeItem('sb-hfizqqbythrxppxhsxed-auth-token')
        setUser(null)
        setSubscriptionActive(false)
        clearSubscriptionCache()
        return { error: null }
      } catch (cleanupErr) {
        console.error('❌ Even emergency cleanup failed:', cleanupErr)
        return { error: err }
      }
    }
  }

  // Sign in with Google
  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin, // This ensures we come back to the same domain
      },
    })
    return { data, error }
  }

  const value = {
    user,
    loading,
    subscriptionActive,
    subscriptionLoading,
    isAuthReady: !loading && !subscriptionLoading,
    hasActiveLicense,
    hadActiveLicense,
    hasActiveSubscription,
    refreshSubscriptionStatus,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    sendVerificationCode,
    verifyCode,
  }

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  )
}